import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Budget.css';

interface BudgetItem {
  _id: string;
  category: string;
  item: string;
  estimatedCost: number;
  actualCost?: number;
  paidAmount: number;
  status: 'pending' | 'partial' | 'paid';
  notes?: string;
}

const Budget: React.FC = () => {
  const { accessToken, logout, loading: authLoading } = useAuth();
  const history = useHistory();
  const [items, setItems] = useState<BudgetItem[]>([]);
  const [familySide, setFamilySide] = useState<'groom' | 'bride' | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [targetBudget, setTargetBudget] = useState(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    category: '',
    item: '',
    estimatedCost: '',
    actualCost: '',
    paidAmount: '',
    status: 'pending',
    notes: '',
  });

  const fetchData = async () => {
    try {
      // Fetch Profile for Theme
      const profileRes = await fetch(`${API_BASE_URL}/api/admin/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const profileData = await profileRes.json();
      if (profileRes.ok) {
        setFamilySide(profileData.admin.familySide);
      }

      // Fetch Budget Items
      const budgetRes = await fetch(`${API_BASE_URL}/api/budget`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const budgetData = await budgetRes.json();
      if (budgetRes.ok) {
        setItems(budgetData.items);
      } else {
        if (budgetRes.status === 401 || budgetRes.status === 403) {
          logout();
          history.push('/login');
        }
        setError(budgetData.message || 'Failed to fetch budget items');
      }

      // Fetch Budget Settings for Categories and Total Target
      const settingsRes = await fetch(`${API_BASE_URL}/api/settings/budget`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const settingsData = await settingsRes.json();
      if (settingsRes.ok) {
        setTargetBudget(settingsData.settings.totalBudget);
        const customCats = settingsData.settings.categoryBudgets.map((c: any) => c.category);
        setCategories(customCats.includes('Other Expenses') ? customCats : [...customCats, 'Other Expenses']);
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) {
      if (accessToken) {
        fetchData();
      } else {
        history.push('/login');
      }
    }
  }, [accessToken, authLoading, history]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/budget`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ...formData,
          estimatedCost: Number(formData.estimatedCost),
          actualCost: formData.actualCost ? Number(formData.actualCost) : undefined,
          paidAmount: Number(formData.paidAmount),
        }),
      });

      if (response.ok) {
        setIsModalOpen(false);
        setFormData({
          category: '',
          item: '',
          estimatedCost: '',
          actualCost: '',
          paidAmount: '',
          status: 'pending',
          notes: '',
        });
        fetchData();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to add item');
      }
    } catch (err) {
      alert('Error adding item');
    }
  };

  const deleteItem = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/budget/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      alert('Error deleting item');
    }
  };

  const totalEstimated = items.reduce((sum, item) => sum + item.estimatedCost, 0);
  const totalPaid = items.reduce((sum, item) => sum + item.paidAmount, 0);
  const totalActual = items.reduce((sum, item) => sum + (item.actualCost || 0), 0);

  if (authLoading || loading) return <div className="budget-container">Loading...</div>;

  return (
    <div className={`admin-dashboard budget-container ${familySide ? `theme-${familySide}` : ''}`}>

      <div className="budget-header">
        <div className="budget-title">
          <h1>Wedding Expenses</h1>
          <p>Track every penny of your special day</p>
        </div>
        <button className="add-expense-btn" onClick={() => setIsModalOpen(true)}>
          + Add New Expense
        </button>
      </div>

      <div className="budget-stats">
        <div className="stat-card primary">
          <h3>Total Budget Limit</h3>
          <div className="amount">₹{targetBudget.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <h3>Total Spent</h3>
          <div className="amount">₹{totalPaid.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <h3>Remaining</h3>
          <div className="amount">₹{(totalEstimated - totalPaid).toLocaleString()}</div>
        </div>
      </div>

      <div className="budget-table-container">
        <table className="budget-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Item</th>
              <th>Estimated</th>
              <th>Actual</th>
              <th>Paid</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item._id}>
                <td>{item.category}</td>
                <td>{item.item}</td>
                <td>₹{item.estimatedCost.toLocaleString()}</td>
                <td>{item.actualCost ? `₹${item.actualCost.toLocaleString()}` : '-'}</td>
                <td>₹{item.paidAmount.toLocaleString()}</td>
                <td>
                  <span className={`status-badge status-${item.status}`}>
                    {item.status}
                  </span>
                </td>
                <td>
                  <button onClick={() => deleteItem(item._id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ff4d4d' }}>
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add New Expense</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Category</label>
                <select name="category" value={formData.category} onChange={handleInputChange} required>
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  {!categories.includes('Other Expenses') && <option value="Other Expenses">Other Expenses</option>}
                </select>
              </div>
              <div className="form-group">
                <label>Item Name</label>
                <input type="text" name="item" value={formData.item} onChange={handleInputChange} placeholder="e.g. Wedding Hall Deposit" required />
              </div>
              <div className="form-group">
                <label>Estimated Cost (₹)</label>
                <input type="number" name="estimatedCost" value={formData.estimatedCost} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Paid Amount (₹)</label>
                <input type="number" name="paidAmount" value={formData.paidAmount} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn" disabled={!formData.category || !formData.item}>Save Expense</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Budget;

